import { useParams } from 'react-router-dom';
import { Spin, Button } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useOrderDetail } from 'commons/hooks/use-order-detail';
import { financialManagementAccountList } from 'apis/fms';
import { FmsAccountManagement } from 'types/fms';
import styles from './index.module.scss';

const formatAmount = (amount?: number | null) => {
  const value = amount ?? 0;
  return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const normalizeKey = (value?: string | null) => {
  if (!value) return '';
  return value.replace(/\s+/g, '').toUpperCase();
};

const SGGXOrderInvoice = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const { omsOrderDetail } = useOrderDetail(
    orderId ? Number(orderId) : undefined,
  );
  const [paymentAccountList, setPaymentAccountList] = useState<
    FmsAccountManagement[]
  >([]);

  const subTotal = useMemo(() => {
    if (!omsOrderDetail) return 0;
    return omsOrderDetail.payAmountActualCurrency ?? 0;
  }, [omsOrderDetail]);

  const selectedPaymentCodes = useMemo(() => {
    const set = new Set<string>();

    if (!omsOrderDetail) return set;

    omsOrderDetail.payList?.forEach((payment) => {
      const typeKey = normalizeKey(payment.payType);
      const modeKey = normalizeKey(payment.payMode);
      if (typeKey) set.add(typeKey);
      if (modeKey) set.add(modeKey);
    });

    return set;
  }, [omsOrderDetail]);

  useEffect(() => {
    if (!omsOrderDetail?.createdFrom) return;

    financialManagementAccountList(omsOrderDetail.createdFrom).then((res) => {
      const list = [...res].sort((a, b) => a.sort - b.sort);
      setPaymentAccountList(list);
    });
  }, [omsOrderDetail?.createdFrom]);

  const paymentOptions = useMemo(
    () =>
      paymentAccountList.map((account) => ({
        key: account.accountCode,
        label: account.accountName,
      })),
    [paymentAccountList],
  );

  const displayPaymentOptions = useMemo(() => {
    return paymentOptions
      .map((option, index) => ({ ...option, index }))
      .sort((a, b) => {
        const aSelected =
          selectedPaymentCodes.has(normalizeKey(a.key)) ||
          selectedPaymentCodes.has(normalizeKey(a.label));
        const bSelected =
          selectedPaymentCodes.has(normalizeKey(b.key)) ||
          selectedPaymentCodes.has(normalizeKey(b.label));

        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;
        return a.index - b.index;
      })
      .map(({ index: _index, ...option }) => option);
  }, [paymentOptions, selectedPaymentCodes]);

  if (!omsOrderDetail) return <Spin />;

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className={styles.container}>
      <div className={styles.invoice}>
        <header className={styles.header}>
          <div className={styles.companyName}>GINZAXIAOMA SG PTE. LTD.</div>
          <div className={styles.companyReg}>Co Reg. No. 202214052E</div>
          <div className={styles.address}>
            391B Orchard Road #23-05 Ngee Ann City Singapore 238874
          </div>
          <div className={styles.contact}>
            Tel : +65 6530 3529 Fax : +65 65303528
          </div>
          <div className={styles.title}>SALES INVOICE</div>
        </header>

        <section className={styles.meta}>
          <div className={styles.toBlock}>
            <div className={styles.label}>To :</div>
            <div className={styles.content}>
              {omsOrderDetail.receiverName}
              {omsOrderDetail.receiverDetailAddress ? (
                <>
                  <br />
                  {omsOrderDetail.receiverDetailAddress}
                </>
              ) : null}
              {omsOrderDetail.receiverCity ||
              omsOrderDetail.receiverProvince ? (
                <>
                  <br />
                  {omsOrderDetail.receiverCity}{' '}
                  {omsOrderDetail.receiverProvince}
                </>
              ) : null}
              {omsOrderDetail.receiverPhone ? (
                <>
                  <br />
                  {omsOrderDetail.receiverPhone}
                </>
              ) : null}
            </div>
          </div>
          <div className={styles.infoBlock}>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Invoice no :</span>
              <span className={styles.infoValue}>
                {omsOrderDetail.paymentTime
                  ? dayjs(omsOrderDetail.paymentTime).format('YYYYMMDD')
                  : ''}
              </span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Date :</span>
              <span className={styles.infoValue}>
                {dayjs(omsOrderDetail.createTime).format('YYYY/M/D')}
              </span>
            </div>
          </div>
        </section>

        <table className={styles.itemsTable}>
          <thead>
            <tr>
              <th className={styles.quantity}>Quantity</th>
              <th className={styles.description}>Description</th>
              <th className={styles.unitPrice}>Unit price (S$)</th>
            </tr>
          </thead>
          <tbody>
            {omsOrderDetail.orderItemList.map((item) => (
              <tr key={item.id}>
                <td className={styles.quantity}>{item.productQuantity}</td>
                <td className={styles.description}>
                  {item.productId}
                  {item.productName ? (
                    <>
                      <br />
                      {item.productName}
                    </>
                  ) : null}
                </td>
                <td className={styles.unitPrice}>
                  {formatAmount(item.realAmountCurrency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className={styles.footerBox}>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Sub Total Amount :</span>
            <span className={styles.summaryValue}>
              {formatAmount(subTotal)}
            </span>
          </div>

          <table className={styles.paymentTable}>
            <tbody>
              <tr>
                <td className={styles.paymentModeCell}>
                  <div className={styles.paymentModeTitle}>Payment mode</div>
                  <div className={styles.paymentOptions}>
                    {displayPaymentOptions.map((option) => (
                      <div className={styles.paymentOption} key={option.key}>
                        <span
                          className={`${styles.checkbox} ${
                            selectedPaymentCodes.has(
                              normalizeKey(option.key),
                            ) ||
                            selectedPaymentCodes.has(normalizeKey(option.label))
                              ? styles.checked
                              : ''
                          }`}
                        />
                        <span className={styles.paymentLabel}>
                          {option.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </td>
                <td className={styles.noteCell}>
                  <div className={styles.noteRow}>
                    <span className={styles.noteCheckbox}>
                      <span
                        className={`${styles.checkbox} ${styles.checked}`}
                      />
                    </span>
                    <span className={styles.noteText}>
                      Goods sold are not eligible for refund, return, or
                      exchange.
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <div className={styles.paynowRow}>
            <span className={styles.paynowLabel}>Paynow</span>
            <span className={styles.paynowValue}>202214052EOCB</span>
          </div>

          <div className={styles.bankTransfer}>
            <div className={styles.bankLabel}>Bank Transfer</div>
            <div className={styles.bankDetails}>
              <div className={styles.bankDetail}>
                <span className={styles.bankDetailLabel}>Beneficiary</span>
                <span className={styles.bankDetailValue}>
                  GINZAXIAOMA SG PTE. LTD.
                </span>
              </div>
              <div className={styles.bankDetail}>
                <span className={styles.bankDetailLabel}>A/C NO</span>
                <span className={styles.bankDetailValue}>595048950001</span>
              </div>
              <div className={styles.bankDetail}>
                <span className={styles.bankDetailLabel}>Bank</span>
                <span className={styles.bankDetailValue}>OCBC</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.printButton}>
        <Button type="primary" onClick={handlePrint}>
          打印发票
        </Button>
      </div>
    </div>
  );
};

export default SGGXOrderInvoice;
